/**
 * Dirty-state registry for the version-skew refresh guard.
 *
 * Safety requirement (locked): a refresh prompt must NEVER reload the page out
 * from under a user who has an in-progress invoice / proposal / editor edit.
 * The version checker is prompt-first — only an explicit Refresh click reloads —
 * but even that click is guarded: if dirty state is present we ask first via a
 * native confirm so an accidental click can't discard work.
 *
 * Any component with unsaved state can register a predicate here. The registry
 * is module-global (single SPA), keyed by a caller-supplied id so a component
 * can update/clear its own entry across re-renders.
 *
 * This intentionally does NOT depend on React — it's a plain module so it can be
 * unit-tested and called from anywhere (hooks, plain handlers).
 */

type DirtyPredicate = () => boolean;

const predicates = new Map<string, DirtyPredicate>();

/**
 * Register (or replace) a dirty-state predicate under `id`.
 * Returns an unregister function (convenient for useEffect cleanup).
 */
export function registerDirtyGuard(id: string, predicate: DirtyPredicate): () => void {
  predicates.set(id, predicate);
  return () => {
    // Only delete if still ours — defends against a stale cleanup clobbering a
    // newer registration under the same id.
    if (predicates.get(id) === predicate) {
      predicates.delete(id);
    }
  };
}

/** Remove a previously-registered guard. */
export function unregisterDirtyGuard(id: string): void {
  predicates.delete(id);
}

/**
 * True if ANY registered predicate currently reports dirty, OR an obvious
 * native editing surface is mid-edit. The native check (a focused, non-empty
 * text input / textarea / contenteditable) is a backstop for forms that never
 * registered a guard — better to over-warn than silently discard work.
 */
export function isAppDirty(): boolean {
  for (const predicate of predicates.values()) {
    try {
      if (predicate()) return true;
    } catch {
      // A throwing predicate must not crash the version check. Treat as
      // not-dirty for that one entry and continue.
    }
  }
  return isNativeEditingSurfaceDirty();
}

/**
 * Backstop heuristic: is the user actively typing into an editable element that
 * has content? Used only when no explicit guard caught the state.
 */
function isNativeEditingSurfaceDirty(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;

  const tag = el.tagName;
  if (tag === 'TEXTAREA') {
    return Boolean((el as HTMLTextAreaElement).value);
  }
  if (tag === 'INPUT') {
    const input = el as HTMLInputElement;
    const type = (input.type || 'text').toLowerCase();
    // Ignore non-data inputs (buttons, checkboxes, the version banner's own
    // controls). Only text-bearing inputs with a value count as dirty.
    const textLike = ['text', 'email', 'number', 'tel', 'url', 'search', 'password'];
    return textLike.includes(type) && Boolean(input.value);
  }
  if (el.isContentEditable) {
    return Boolean(el.textContent && el.textContent.trim().length > 0);
  }
  return false;
}

/** Test-only: clear all registered predicates. */
export function __resetDirtyGuardsForTest(): void {
  predicates.clear();
}
